/** Local, opt-in storage traffic diagnostics. No payloads or credentials are retained. */
export interface SyncTrafficEntry {
  at: string;
  method: string;
  operation: string;
  workspace: string;
  document?: string;
  documents?: Array<{ id: string; jsonBytes: number; snapshots: number; snapshotBytes: number; updates: number; updateBytes: number; resets: number }>;
  status: number;
  requestBytes: number;
  responseBytes: number;
  snapshots: number;
  snapshotBytes: number;
  updates: number;
  updateBytes: number;
  resets: number;
}

const ENABLE_KEY = 'yada:sync-diagnostics:enabled';
const ENTRIES_KEY = 'yada:sync-diagnostics:entries';
const MAX_ENTRIES = 1000;

function storage(): Storage | undefined {
  try { return typeof localStorage === 'undefined' ? undefined : localStorage; } catch { return undefined; }
}

export function syncDiagnosticsEnabled(): boolean {
  return storage()?.getItem(ENABLE_KEY) === 'true';
}

export function enableSyncDiagnostics(enabled = true): void {
  storage()?.setItem(ENABLE_KEY, String(enabled));
}

export function clearSyncDiagnostics(): void {
  storage()?.removeItem(ENTRIES_KEY);
}

export function getSyncDiagnostics(): SyncTrafficEntry[] {
  try {
    const entries = JSON.parse(storage()?.getItem(ENTRIES_KEY) ?? '[]');
    return Array.isArray(entries) ? entries as SyncTrafficEntry[] : [];
  } catch { return []; }
}

export function summarizeSyncDiagnostics(): {
  requests: number; requestBytes: number; responseBytes: number;
  snapshots: number; snapshotBytes: number; updates: number; updateBytes: number; resets: number;
  byOperation: Record<string, { requests: number; requestBytes: number; responseBytes: number; resets: number }>;
  byDocument: Record<string, { requests: number; responseBytes: number; snapshots: number; snapshotBytes: number; updates: number; updateBytes: number; resets: number }>;
} {
  const entries = getSyncDiagnostics();
  const result = {
    requests: entries.length, requestBytes: 0, responseBytes: 0,
    snapshots: 0, snapshotBytes: 0, updates: 0, updateBytes: 0, resets: 0,
    byOperation: {} as Record<string, { requests: number; requestBytes: number; responseBytes: number; resets: number }>,
    byDocument: {} as Record<string, { requests: number; responseBytes: number; snapshots: number; snapshotBytes: number; updates: number; updateBytes: number; resets: number }>,
  };
  for (const entry of entries) {
    result.requestBytes += entry.requestBytes;
    result.responseBytes += entry.responseBytes;
    result.snapshots += entry.snapshots ?? 0;
    result.updates += entry.updates ?? 0;
    result.snapshotBytes += entry.snapshotBytes;
    result.updateBytes += entry.updateBytes;
    result.resets += entry.resets;
    const operation = result.byOperation[entry.operation] ??= { requests: 0, requestBytes: 0, responseBytes: 0, resets: 0 };
    operation.requests++;
    operation.requestBytes += entry.requestBytes;
    operation.responseBytes += entry.responseBytes;
    operation.resets += entry.resets;
    const docs = entry.documents ?? (entry.document ? [{ id: entry.document, jsonBytes: entry.responseBytes,
      snapshots: entry.snapshots ?? 0, snapshotBytes: entry.snapshotBytes, updates: entry.updates ?? 0, updateBytes: entry.updateBytes, resets: entry.resets }] : []);
    for (const item of docs) {
      const doc = result.byDocument[item.id] ??= { requests: 0, responseBytes: 0, snapshots: 0, snapshotBytes: 0, updates: 0, updateBytes: 0, resets: 0 };
      doc.requests++;
      doc.responseBytes += item.jsonBytes;
      doc.snapshots += item.snapshots;
      doc.updates += item.updates;
      doc.snapshotBytes += item.snapshotBytes;
      doc.updateBytes += item.updateBytes;
      doc.resets += item.resets;
    }
  }
  return result;
}

export function recordSyncTraffic(entry: SyncTrafficEntry): void {
  if (!syncDiagnosticsEnabled()) return;
  try {
    const target = storage();
    target?.setItem(ENTRIES_KEY, JSON.stringify([...getSyncDiagnostics(), entry].slice(-MAX_ENTRIES)));
  } catch { /* Diagnostics must never interrupt sync. */ }
}
